"use client";

import { Slot } from "@radix-ui/react-slot";
import * as React from "react";

import { type RecipeVariantProps, css, cva } from "@/../styled-system/css";
import type { HTMLStyledProps } from "@/../styled-system/jsx";

const button = cva({
  base: {
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "2",
    flexShrink: "0",
    rounded: "lg",
    cursor: "pointer",
    py: "2.5",
    px: "5",
    fontWeight: "semibold",
    _disabled: {
      cursor: "not-allowed",
      backgroundColor: "brandLightGray",
      borderColor: "brandLightGray",
      color: "white",
    },
  },
  variants: {
    width: {
      icon: { width: "fit", py: "2", px: "3", fontSize: "sm" },
      fit: { width: "fit" },
      fill: { width: "100%" },
      auto: { width: "100%", sm: { width: "fit" } },
    },
    variant: {
      black: {
        backgroundColor: "black",
        color: "white",
        _hover: {
          backgroundColor: "black/70",
        },
      },
      brandBlue: {
        backgroundColor: "brandBlue",
        color: "white",
        _hover: {
          backgroundColor: "brandBlue/70",
        },
      },
      brandDarkCyan: {
        backgroundColor: "brandDarkCyan",
        color: "white",
        _hover: {
          backgroundColor: "brandDarkCyan/70",
        },
      },
      outline: {
        borderWidth: "1px",
        borderColor: "gray.300",
        color: "gray.700",
        _hover: {
          backgroundColor: "gray.100/75",
        },
      },
      danger: {
        backgroundColor: "red.600",
        color: "white",
        _hover: {
          backgroundColor: "red.600/70",
        },
      },
      ghost: {
        color: "gray.600",
        _hover: {
          backgroundColor: "gray.600/10",
          color: "black",
        },
      },
      gray: {
        backgroundColor: "gray.100",
        color: "gray.800",
      },
      memberLogIn: {
        backgroundColor: "brandBlack",
        color: "white",
        rounded: "md",
        _hover: {
          backgroundColor: "brandTeal",
        },
        width: "192px",
        height: "52px",
        transition: "all 0.3s ease-in-out",
      },
      nonMemberEnter: {
        backgroundColor: "white",
        borderColor: "black",
        borderWidth: "2px",
        rounded: "md",
        _hover: {
          borderColor: "brandTeal",
        },
        width: "192px",
        height: "52px",
        transition: "all 0.3s ease-in-out",
      },
      primary: {
        backgroundColor: "brandTeal",
        color: "white",
        rounded: "md",
        _enabled: {
          _hover: {
            backgroundColor: "brandBlack",
          },
          _active: {
            color: "white",
            backgroundColor: "brandLightBrown",
          },
        },
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        transition: "all 0.3s ease-in-out",
      },
      secondary: {
        backgroundColor: "white",
        color: "brandBlack",
        borderColor: "black",
        borderWidth: "1px",
        rounded: "md",
        _enabled: {
          _hover: {
            color: "brandTeal",
            borderColor: "brandTeal",
          },
          _active: {
            color: "white",
            backgroundColor: "brandTeal",
            borderColor: "brandTeal",
            _hover: {
              color: "white",
            },
          },
        },
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        transition: "all 0.3s ease-in-out",
      },
      linkPrimary: {
        backgroundColor: "brandBlack",
        color: "white",
        rounded: "full",
        _enabled: {
          _hover: {
            backgroundColor: "brandLightBrown",
          },
        },
        transition: "all 0.3s ease-in-out",
      },
      linkSecondary: {
        backgroundColor: "white",
        color: "brandBlack",
        borderColor: "black",
        borderWidth: "2px",
        rounded: "full",
        _enabled: {
          _hover: {
            color: "brandLightBrown",
            borderColor: "brandLightBrown",
          },
        },
        transition: "all 0.3s ease-in-out",
      },
    },
  },
});

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> &
    RecipeVariantProps<typeof button> & {
      css?: object;
      asChild?: boolean;
      children?: React.ReactNode;
    }
>(({ width, variant, css: cssProp = {}, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  const className = css(
    button.raw({ width: width, variant: variant }),
    cssProp,
  );
  return (
    <Comp
      type={props.type || "button"}
      className={className}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = "Button";

export type ButtonProps = HTMLStyledProps<typeof Button>;
export default Button;
